import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services';
import { utils } from '../../utils';
import FilterPanel from '../../components/Common/FilterPanel';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

interface CorrectiveMaintenanceItem {
  id: number;
  veiculo: string;
  descricao: string;
  data_solicitacao: string;
  data_inicio?: string;
  data_conclusao?: string;
  custo_estimado: number;
  custo_real?: number;
  fornecedor: string;
  status: string;
  prioridade: string;
  tipo_problema: string;
  sintomas: string;
  causa_raiz?: string;
  solucao_aplicada?: string;
  tempo_total?: number;
  observacoes?: string;
}

const CorrectiveMaintenance: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [problemTypeFilter, setProblemTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Hook de infinite scroll
  const {
    items: maintenances,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore
  } = useInfiniteScroll<CorrectiveMaintenanceItem>({
    fetchFunction: async (page, limit, filters) => {
      try {
        const response = await apiService.getCorrectiveMaintenances({
          page,
          limit
        });

        if (response.success && response.data) {
          const apiMaintenances = Array.isArray(response.data.maintenances) ? response.data.maintenances : [];

          const mappedMaintenances: CorrectiveMaintenanceItem[] = apiMaintenances.map((item: any) => ({
            id: item.id,
            veiculo: item.veiculo || item.veiculo_placa || 'N/A',
            descricao: item.descricao || '',
            data_solicitacao: item.data_solicitacao,
            data_inicio: item.data_inicio,
            data_conclusao: item.data_conclusao,
            custo_estimado: item.custo_estimado || 0,
            custo_real: item.custo_real,
            fornecedor: item.fornecedor || item.fornecedor_nome || 'N/A',
            status: item.status || 'Solicitada',
            prioridade: item.prioridade || 'Média',
            tipo_problema: item.tipo_problema || '',
            sintomas: item.sintomas || '',
            causa_raiz: item.causa_raiz,
            solucao_aplicada: item.solucao_aplicada,
            tempo_total: item.tempo_total,
            observacoes: item.observacoes
          }));

          return {
            success: true,
            data: {
              items: mappedMaintenances,
              pagination: {
                total: response.data.pagination?.total || mappedMaintenances.length
              }
            }
          };
        }

        throw new Error('Resposta inválida da API');
      } catch (error) {
        console.error('Erro ao carregar manutenções corretivas:', error);
        return {
          success: false,
          data: [],
          message: 'Erro ao carregar manutenções corretivas'
        };
      }
    },
    itemsPerPage: 10,
    filters: {},
    onError: (error) => {
      utils.showNotification(error, { type: 'error' });
    }
  });

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Solicitada': 'status-info',
      'Em Diagnóstico': 'status-secondary',
      'Em Andamento': 'status-warning',
      'Aguardando Peças': 'status-warning',
      'Concluída': 'status-success',
      'Cancelada': 'status-danger'
    };
    return statusMap[status] || 'status-secondary';
  };

  const getPriorityClass = (prioridade: string): string => {
    const priorityMap: Record<string, string> = {
      'Crítica': 'status-danger',
      'Urgente': 'status-danger',
      'Alta': 'status-warning',
      'Média': 'status-info',
      'Baixa': 'status-success'
    };
    return priorityMap[prioridade] || 'status-secondary';
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value);
  };

  const filteredMaintenances = maintenances.filter(maintenance => {
    const matchesSearch = maintenance.veiculo.toLowerCase().includes(filter.toLowerCase()) ||
                         maintenance.descricao.toLowerCase().includes(filter.toLowerCase()) ||
                         maintenance.fornecedor.toLowerCase().includes(filter.toLowerCase()) ||
                         maintenance.tipo_problema.toLowerCase().includes(filter.toLowerCase());
    const matchesVehicle = !vehicleFilter || maintenance.veiculo === vehicleFilter;
    const matchesStatus = !statusFilter || maintenance.status === statusFilter;
    const matchesPriority = !priorityFilter || maintenance.prioridade === priorityFilter;
    const matchesProblemType = !problemTypeFilter || maintenance.tipo_problema === problemTypeFilter;
    return matchesSearch && matchesVehicle && matchesStatus && matchesPriority && matchesProblemType;
  });

  const viewMaintenance = (id: number) => {
    navigate(`/manutencoes-corretivas/visualizar/${id}`);
  };

  const editMaintenance = (id: number) => {
    utils.showNotification(`Editando manutenção corretiva ${id}`, { type: 'info' });
  };

  const deleteMaintenance = (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm('Tem certeza que deseja excluir esta manutenção corretiva?')) {
      utils.showNotification('Manutenção corretiva excluída com sucesso!', { type: 'success' });
      window.location.reload();
    }
  };


  const getVehicles = () => {
    const vehicles = Array.from(new Set(maintenances.map(m => m.veiculo)));
    return vehicles;
  };

  const getStatuses = () => {
    const statuses = Array.from(new Set(maintenances.map(m => m.status)));
    return statuses;
  };

  const getPriorities = () => {
    const priorities = Array.from(new Set(maintenances.map(m => m.prioridade)));
    return priorities;
  };

  const getProblemTypes = () => {
    const problemTypes = Array.from(new Set(maintenances.map(m => m.tipo_problema)));
    return problemTypes;
  };

  const getDowntimeClass = (tempo: number | undefined): string => {
    if (!tempo) return 'status-secondary';
    if (tempo > 8) return 'status-danger';
    if (tempo > 4) return 'status-warning';
    return 'status-success';
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Manutenções Corretivas</h1>
          <button className="btn btn-primary" disabled>
            <i className="fas fa-plus"></i>
            Nova Manutenção Corretiva
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando manutenções corretivas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Manutenções Corretivas</h1>
        <button 
          className="btn btn-primary"
          onClick={() => navigate('/manutencoes-corretivas/novo')}
        >
          <i className="fas fa-plus"></i>
          Nova Manutenção Corretiva
        </button>
      </div>

      <div className="page-content">

        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Controle de Manutenções Corretivas</h2>
            <FilterPanel
              fields={[
                {
                  type: 'text',
                  label: 'Buscar',
                  value: filter,
                  onChange: setFilter,
                  placeholder: 'Veículo, descrição, fornecedor ou problema...'
                },
                {
                  type: 'select',
                  label: 'Veículo',
                  value: vehicleFilter,
                  onChange: setVehicleFilter,
                  options: [{ value: '', label: 'Todos' }, ...getVehicles().map(vehicle => ({ value: vehicle, label: vehicle }))]
                },
                {
                  type: 'select',
                  label: 'Tipo de Problema',
                  value: problemTypeFilter,
                  onChange: setProblemTypeFilter,
                  options: [{ value: '', label: 'Todos' }, ...getProblemTypes().map(type => ({ value: type, label: type }))]
                },
                {
                  type: 'select',
                  label: 'Status',
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [{ value: '', label: 'Todos' }, ...getStatuses().map(status => ({ value: status, label: status }))]
                },
                {
                  type: 'select',
                  label: 'Prioridade',
                  value: priorityFilter,
                  onChange: setPriorityFilter,
                  options: [{ value: '', label: 'Todas' }, ...getPriorities().map(priority => ({ value: priority, label: priority }))]
                }
              ]}
              onFilter={() => {}}
              onClear={() => {
                setFilter('');
                setVehicleFilter('');
                setProblemTypeFilter('');
                setStatusFilter('');
                setPriorityFilter('');
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Manutenções Corretivas"
            />
          </div>

          <div className="card-body">
            {/* Contador de resultados */}
            {filteredMaintenances.length > 0 && (
              <ResultsCounter
                displayedItems={displayedItems}
                totalItems={totalItems}
                isLoading={isLoading}
                label="manutenções corretivas"
              />
            )}

            {filteredMaintenances.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-tools empty-state-icon"></i>
                <h3>Nenhuma manutenção corretiva encontrada</h3>
                <p>{filter || vehicleFilter || problemTypeFilter || statusFilter || priorityFilter ? 'Tente ajustar os filtros de busca.' : 'Registre a primeira manutenção corretiva do sistema.'}</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/manutencoes-corretivas/novo')}
                >
                  Registrar Primeira Manutenção
                </button>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Veículo</th>
                      <th>Descrição</th>
                      <th>Tipo Problema</th>
                      <th>Sintomas</th>
                      <th>Data Solicitação</th>
                      <th>Data Início</th>
                      <th>Data Conclusão</th>
                      <th>Tempo Total (h)</th>
                      <th>Custo Estimado</th>
                      <th>Custo Real</th>
                      <th>Fornecedor</th>
                      <th>Status</th>
                      <th>Prioridade</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMaintenances.map(maintenance => (
                      <tr key={maintenance.id}>
                        <td>
                          <strong>{maintenance.veiculo}</strong>
                        </td>
                        <td>
                          <div style={{ maxWidth: '200px' }}>
                            {maintenance.descricao}
                          </div>
                        </td>
                        <td>
                          <span className="status-badge status-info">
                            {maintenance.tipo_problema}
                          </span>
                        </td>
                        <td>
                          <div style={{ maxWidth: '150px', fontSize: '0.8rem' }}>
                            {maintenance.sintomas}
                          </div>
                        </td>
                        <td>
                          {formatDateTime(maintenance.data_solicitacao)}
                        </td>
                        <td>
                          {maintenance.data_inicio ? formatDateTime(maintenance.data_inicio) : '-'}
                        </td>
                        <td>
                          {maintenance.data_conclusao ? formatDateTime(maintenance.data_conclusao) : '-'}
                        </td>
                        <td>
                          {maintenance.tempo_total ? (
                            <span className={`status-badge ${getDowntimeClass(maintenance.tempo_total)}`}>
                              {formatNumber(maintenance.tempo_total)}h
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          {formatCurrency(maintenance.custo_estimado)}
                        </td>
                        <td>
                          {maintenance.custo_real ? (
                            <strong>{formatCurrency(maintenance.custo_real)}</strong>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          {maintenance.fornecedor}
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusClass(maintenance.status)}`}>
                            {maintenance.status}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${getPriorityClass(maintenance.prioridade)}`}>
                            {maintenance.prioridade}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="btn-view"
                              onClick={() => viewMaintenance(maintenance.id)}
                              title="Visualizar"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button 
                              className="btn-edit"
                              onClick={() => editMaintenance(maintenance.id)}
                              title="Editar"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button 
                              className="btn-delete"
                              onClick={() => deleteMaintenance(maintenance.id)}
                              title="Excluir"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Botão "Ver Mais" para carregamento manual */}
                <LoadMoreButton
                  onClick={loadMore}
                  isLoading={isLoading}
                  hasMore={hasMore}
                />
              </div>
            )}
          </div>
        </div>

        {/* Estatísticas das manutenções corretivas */}
        <div className="dashboard-grid" style={{ marginTop: '20px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-tools"></i>
            </div>
            <div className="stat-value">
              {maintenances.length}
            </div>
            <div className="stat-label">Total de Corretivas</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-value">
              {maintenances.filter(m => m.status === 'Concluída').length}
            </div>
            <div className="stat-label">Concluídas</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-clock"></i>
            </div>
            <div className="stat-value">
              {maintenances.filter(m => m.status === 'Em Andamento').length}
            </div>
            <div className="stat-label">Em Andamento</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-money-bill-wave"></i>
            </div>
            <div className="stat-value">
              {formatCurrency(maintenances.reduce((total, m) => total + Number(m.custo_real || m.custo_estimado || 0), 0))}
            </div>
            <div className="stat-label">Custo Total</div>
          </div>
        </div>

        {/* Distribuição por tipo de problema */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Distribuição por Tipo de Problema</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              {getProblemTypes().map(problemType => {
                const typeMaintenances = maintenances.filter(m => m.tipo_problema === problemType);
                const percentage = Math.round((typeMaintenances.length / maintenances.length) * 100);
                const totalCost = typeMaintenances.reduce((total, m) => total + Number(m.custo_real || m.custo_estimado || 0), 0);
                const avgTime = typeMaintenances.reduce((total, m) => total + Number(m.tempo_total || 0), 0) / typeMaintenances.length;
                
                return (
                  <div key={problemType} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>{problemType}</span>
                      <span style={{ color: '#04C64F', fontWeight: 'bold' }}>{typeMaintenances.length}</span>
                    </div>
                    <div style={{ background: '#e9ecef', borderRadius: '4px', height: '8px' }}>
                      <div 
                        style={{ 
                          background: 'linear-gradient(135deg, #04C64F, #03a542)', 
                          height: '100%', 
                          borderRadius: '4px', 
                          width: `${percentage}%` 
                        }}
                      ></div>
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#7f8c8d' }}>
                      <div>Percentual: <strong>{percentage}%</strong></div>
                      <div>Custo total: <strong>{formatCurrency(totalCost)}</strong></div>
                      <div>Tempo médio: <strong>{formatNumber(avgTime)}h</strong></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tempo de inatividade por veículo */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Tempo de Inatividade por Veículo</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {getVehicles().map(vehicle => {
                const vehicleMaintenances = maintenances.filter(m => m.veiculo === vehicle);
                const totalDowntime = vehicleMaintenances.reduce((total, m) => total + Number(m.tempo_total || 0), 0);
                const totalCost = vehicleMaintenances.reduce((total, m) => total + Number(m.custo_real || m.custo_estimado || 0), 0);
                const completed = vehicleMaintenances.filter(m => m.status === 'Concluída').length;
                const inProgress = vehicleMaintenances.filter(m => m.status === 'Em Andamento').length;
                
                return (
                  <div key={vehicle} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>{vehicle}</span>
                      <span style={{ color: '#04C64F', fontWeight: 'bold' }}>{vehicleMaintenances.length}</span>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                      <div>Concluídas: <strong>{completed}</strong></div>
                      <div>Em andamento: <strong>{inProgress}</strong></div>
                      <div>Tempo total: <strong>{formatNumber(totalDowntime)}h</strong></div>
                      <div>Custo total: <strong>{formatCurrency(totalCost)}</strong></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Manutenções urgentes */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Manutenções Urgentes</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {maintenances
                .filter(m => m.prioridade === 'Urgente')
                .sort((a, b) => new Date(a.data_solicitacao).getTime() - new Date(b.data_solicitacao).getTime())
                .slice(0, 4)
                .map(maintenance => (
                  <div key={maintenance.id} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>{maintenance.veiculo}</span>
                      <span className={`status-badge ${getStatusClass(maintenance.status)}`}>
                        {maintenance.status}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '10px' }}>
                      {maintenance.descricao}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                      <div>Problema: <strong>{maintenance.tipo_problema}</strong></div>
                      <div>Solicitação: <strong>{formatDateTime(maintenance.data_solicitacao)}</strong></div>
                      <div>Tempo: <strong>{maintenance.tempo_total ? `${formatNumber(maintenance.tempo_total)}h` : 'N/A'}</strong></div>
                      <div>Custo: <strong>{formatCurrency(maintenance.custo_real || maintenance.custo_estimado)}</strong></div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorrectiveMaintenance;
